import { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { PresetGroup, Preset } from '../lib/supabase';
import { createGroup, updateGroup, getBuiltInPresets, getUserPresets } from '../lib/api';

interface GroupEditorProps {
  group: (PresetGroup & { presets: Preset[] }) | null;
  onSave: () => void;
  onCancel: () => void;
}

function generateGroupName(presets: Preset[]): string {
  if (presets.length === 0) return 'New Group';
  if (presets.length === 1) return presets[0].name;
  if (presets.length === 2) return `${presets[0].name} + ${presets[1].name}`;
  return `${presets[0].name} + ${presets.length - 1} more`;
}

export function GroupEditor({ group, onSave, onCancel }: GroupEditorProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPresetIds, setSelectedPresetIds] = useState<Set<string>>(new Set());
  const [availablePresets, setAvailablePresets] = useState<Preset[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPresets();
  }, []);

  useEffect(() => {
    if (group) {
      setName(group.name);
      setDescription(group.description || '');
      setSelectedPresetIds(new Set(group.presets.map(p => p.id)));
    } else {
      const selected = availablePresets.filter(p => selectedPresetIds.has(p.id));
      if (selectedPresetIds.size > 0 && !name) {
        setName(generateGroupName(selected));
      }
    }
  }, [group, availablePresets]);

  const loadPresets = async () => {
    setLoading(true);
    try {
      const [builtin, user] = await Promise.all([
        getBuiltInPresets(),
        getUserPresets(),
      ]);
      setAvailablePresets([...builtin, ...user]);
    } finally {
      setLoading(false);
    }
  };

  const togglePreset = (presetId: string) => {
    const newSelected = new Set(selectedPresetIds);
    if (newSelected.has(presetId)) {
      newSelected.delete(presetId);
    } else {
      newSelected.add(presetId);
    }
    setSelectedPresetIds(newSelected);

    if (!group) {
      const selected = availablePresets.filter(p => newSelected.has(p.id));
      setName(generateGroupName(selected));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedPresetIds.size === 0) {
      alert('Please select at least one supplement');
      return;
    }

    if (!name.trim()) {
      alert('Please enter a group name');
      return;
    }

    setSaving(true);
    try {
      if (group) {
        await updateGroup(group.id, name.trim(), Array.from(selectedPresetIds), description.trim() || undefined);
      } else {
        await createGroup(name.trim(), Array.from(selectedPresetIds), description.trim() || undefined);
      }
      onSave();
    } catch (error: any) {
      alert(error.message || 'Error saving group');
    } finally {
      setSaving(false);
    }
  };

  const filteredPresets = availablePresets.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.tag.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 animate-scale-in">
      <div className="bg-white rounded-2xl shadow-xl">
        <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-200 p-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900">
            {group ? 'Edit Group' : 'New Group'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 text-gray-500 hover:bg-gray-100 hover:scale-110 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center active:scale-95 transition-all duration-200"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:scale-[1.01] text-base transition-all duration-200"
              placeholder="e.g., Morning Stack"
            />
            <p className="text-xs text-gray-500 mt-1">
              Auto-generated from selected supplements, but you can change it
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:scale-[1.01] text-base transition-all duration-200"
              placeholder="e.g., Supplements to take first thing in the morning"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Select Supplements ({selectedPresetIds.size} selected)
              </label>
            </div>

            <input
              type="text"
              placeholder="Search supplements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:scale-[1.01] mb-3 text-base transition-all duration-200"
            />

            <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
              {filteredPresets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? 'No supplements found' : 'No supplements available'}
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredPresets.map((preset) => {
                    const isSelected = selectedPresetIds.has(preset.id);
                    return (
                      <label
                        key={preset.id}
                        className="flex items-center gap-3 p-4 hover:bg-blue-50 cursor-pointer active:bg-blue-100 transition-all duration-200 min-h-[60px]"
                      >
                        <div className="relative flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => togglePreset(preset.id)}
                            className="w-6 h-6 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                          />
                          {isSelected && (
                            <Check className="absolute inset-0 m-auto text-blue-600 pointer-events-none" size={16} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">{preset.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{preset.tag}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="sticky bottom-0 bg-white pt-4 pb-2 -mx-4 px-4 border-t border-gray-200">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 hover:shadow-md active:scale-95 transition-all duration-200 min-h-[48px]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || selectedPresetIds.size === 0}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-all duration-200 min-h-[48px]"
              >
                {saving ? 'Saving...' : 'Save Group'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
